import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { deriveGuidePartnersState } from '../guidePartnersRoomModel';
import {
  GUEST_GUIDES_TIMEOUT_MS,
  GUIDE_PHOTO_MAX_BYTES,
  INVITE_STAGE_OPTIONS,
  STATUS_OPTIONS,
} from '../guest-guides/constants';

const GUIDE_NEXT_MOVE_LIMIT = 5;
const SCHEDULING_CONTEXT_DEFAULT_LIMIT = 4;
const GUIDE_SECTION_DEFAULT_EXPANDED = {
  ready_to_schedule: true,
  onboarding_sent: true,
  prospects_not_invited: true,
  invited: true,
  backburner: false,
  scheduled: false,
  declined: false,
  delivery_issues: false,
};

const normalizeGuideInviteStage = (guide = {}) => (guide?.invite_stage || 'not_invited').trim().toLowerCase() || 'not_invited';
const normalizeGuideStatus = (guide = {}) => (guide?.status || 'prospect').trim().toLowerCase() || 'prospect';

const createGuideFormData = () => ({
  name: '',
  email: '',
  phone: '',
  photo_url: '',
  bio: '',
  role: 'Guide Partner',
  account_roles: [],
  focus_areas: [],
  expertise_text: '',
  birth_year: '',
  location: '',
  linkedin: '',
  facebook: '',
  twitter: '',
  youtube: '',
  bluesky: '',
  website: '',
  status: 'prospect',
  invite_stage: 'not_invited',
  notes: '',
});

const createEmailData = () => ({
  subject: '',
  body: '',
  documents: [],
});

const createScheduleData = () => ({
  scheduled_date: '',
  scheduled_topic: '',
});

const createCampaignForm = () => ({
  sequence_id: '',
  step_key: '',
  delivery_mode: 'send_now',
  auto_chain_sequence: true,
  batch_size: 20,
  cadence_type: 'daily',
  cadence_interval: 1,
  start_date: '',
  start_time: '07:00',
  chain_delays: {
    email_2: 1,
    email_3: 4,
  },
});

const createReactivationForm = () => ({
  guide_ids: [],
  subject: '',
  body: '',
});

const todayDateInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const createManualContactForm = () => ({
  contacted_on: todayDateInputValue(),
  note: '',
});

const REACTIVATION_DEFAULT_SUBJECT = 'A personal follow-up from Kevin';
const REACTIVATION_DEFAULT_BODY = `Hi [First Name],

I wanted to follow up personally.

Some time ago I reached out because I believed your life and your story could be a real gift to younger dads inside Dad Partners. I still believe that.

What dads often need most is not a polished teacher or expert, but an older brother or sister in Christ who can honestly share one area of life where God met them, changed them, and carried them through. A story like that can give a younger dad hope, perspective, and courage in a way almost nothing else can.

If part of what has held you back is not knowing exactly what story to tell or how to tell it, that is okay. We can help with that. We can help you think through what part of your story would be most helpful, and how to share it in a way that is simple, honest, and meaningful.

And if life simply got busy, that's okay too. If you're still open to it, I would love to talk with you and help you take the next step.

If you would be open to talking about it, just call me:
919-937-1711

You can also revisit the Guide Partner invitation here:
[Guide Partners Invite Video]

And if sharing your story is not the right fit or not the right season, there are still other ways you could bless the work of Dad Partners. If you know a younger dad who would be a good fit for Dad Partners, please let me know, or feel free to forward him this page:
[Refer a Dad]

You may also want to stay connected to what we are building so you can pray for us and for the dads we are serving. And if you feel led, you may want to support the mission financially so more dads can receive this kind of help.

Either way, I would be grateful to hear from you.

Grateful,
Kevin`;

const STALE_INVITED_DAYS = 30;

const formatGuideQueueDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export function useGuidePartnersAdminState({
  adminFetch,
  authLoading = false,
  hasAdminToken = false,
  activeCampaigns = [],
  deliveryIssues = [],
  guideSummary = null,
  refreshGuideSummary = null,
  workflowKey = null,
  workflowRequestKey = null,
}) {
  const [guides, setGuides] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [showReactivationModal, setShowReactivationModal] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [selectedGuideIds, setSelectedGuideIds] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [formData, setFormData] = useState(createGuideFormData);
  const [emailData, setEmailData] = useState(createEmailData);
  const [scheduleData, setScheduleData] = useState(createScheduleData);
  const [importFileName, setImportFileName] = useState('');
  const [parsedImportGuides, setParsedImportGuides] = useState([]);
  const [importParseError, setImportParseError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingGuidePhotoId, setUploadingGuidePhotoId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loadingSequences, setLoadingSequences] = useState(false);
  const [previewingCampaign, setPreviewingCampaign] = useState(false);
  const [campaignSubmitting, setCampaignSubmitting] = useState(false);
  const [campaignForm, setCampaignForm] = useState(createCampaignForm);
  const [reactivationForm, setReactivationForm] = useState(createReactivationForm);
  const [showManualContactModal, setShowManualContactModal] = useState(false);
  const [manualContactForm, setManualContactForm] = useState(createManualContactForm);
  const [manualContactSubmitting, setManualContactSubmitting] = useState(false);
  const [showAllReadyToSchedule, setShowAllReadyToSchedule] = useState(false);
  const [expandedGuideSections, setExpandedGuideSections] = useState(GUIDE_SECTION_DEFAULT_EXPANDED);
  const guestGuideAbortRef = useRef(null);
  const roleSaveIdempotencyRef = useRef(null);
  const outreachSectionRef = useRef(null);

  const validStatusValues = useMemo(
    () => new Set(STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => option.value)),
    []
  );
  const validInviteStageValues = useMemo(
    () => new Set(INVITE_STAGE_OPTIONS.map((option) => option.value)),
    []
  );

  const getGuidePriority = (guide) => {
    const inviteStage = guide.invite_stage || 'not_invited';
    const status = guide.status || 'prospect';

    if (status === 'scheduled' || inviteStage === 'scheduled') return 0;
    if (inviteStage === 'onboarding_completed') return 1;
    if (status === 'active') return 2;
    if (inviteStage === 'onboarding_sent') return 3;
    if (status === 'invited' || inviteStage === 'invited') return 4;
    if (inviteStage === 'interested') return 5;
    if (status === 'completed' || inviteStage === 'completed') return 6;
    if (inviteStage === 'backburner') return 7;
    if (inviteStage === 'declined') return 8;
    return 9;
  };

  const prioritizeGuides = useCallback((guidesList = []) => [...guidesList].sort((a, b) => {
    const priorityDiff = getGuidePriority(a) - getGuidePriority(b);
    if (priorityDiff !== 0) return priorityDiff;

    if (sortBy === 'name') {
      return (a.name || '').localeCompare(b.name || '');
    }

    if (sortBy === 'scheduled_date') {
      return (a.scheduled_date || '').localeCompare(b.scheduled_date || '');
    }

    return (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '');
  }), [sortBy]);

  const parseCsvRow = (row) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < row.length; i += 1) {
      const char = row[i];
      const nextChar = row[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current.trim());
    return values.map((value) => value.replace(/^"(.*)"$/, '$1').trim());
  };

  const normalizeImportHeader = (header) => {
    const normalized = header.toLowerCase().trim().replace(/[\s-]+/g, '_');
    const aliases = {
      firstname: 'first_name',
      lastname: 'last_name',
      full_name: 'name',
      phone_number: 'phone',
      mobile: 'phone',
      mobile_number: 'phone',
      focusareas: 'focus_areas',
      invitestage: 'invite_stage',
      invitation_stage: 'invite_stage',
    };

    return aliases[normalized] || normalized;
  };

  const normalizeImportWorkflowValue = (value) => String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

  const normalizeImportInviteStage = (value) => {
    const normalized = normalizeImportWorkflowValue(value);
    const aliases = {
      ready_to_schedule: 'onboarding_completed',
      onboarding_complete: 'onboarding_completed',
      onboarding_done: 'onboarding_completed',
    };
    return aliases[normalized] || normalized;
  };

  const parseImportCsv = (text) => {
    const rows = text
      .split(/\r?\n/)
      .map((row) => row.trim())
      .filter(Boolean);

    if (rows.length < 2) {
      throw new Error('CSV must include a header row and at least one data row.');
    }

    const headers = parseCsvRow(rows[0]).map(normalizeImportHeader);
    const hasNameColumn = headers.includes('name');
    const hasFirstNameColumn = headers.includes('first_name');
    const hasLastNameColumn = headers.includes('last_name');

    if (!headers.includes('email')) {
      throw new Error('Missing required column: email');
    }

    if (!hasNameColumn && !hasFirstNameColumn && !hasLastNameColumn) {
      throw new Error('Include either a name column or first_name / last_name columns.');
    }

    const importedGuides = rows
      .slice(1)
      .map((row, index) => {
        const values = parseCsvRow(row);
        const item = headers.reduce((acc, header, valueIndex) => {
          acc[header] = values[valueIndex] || '';
          return acc;
        }, {});

        const fullName = item.name || [item.first_name, item.last_name].filter(Boolean).join(' ').trim();
        const normalizedStatus = normalizeImportWorkflowValue(item.status || 'prospect') || 'prospect';
        const normalizedInviteStage = normalizeImportInviteStage(item.invite_stage || 'not_invited') || 'not_invited';

        if (item.status && !validStatusValues.has(normalizedStatus)) {
          throw new Error(`Invalid status "${item.status}" on row ${index + 2}.`);
        }

        if (item.invite_stage && !validInviteStageValues.has(normalizedInviteStage)) {
          throw new Error(`Invalid invite_stage "${item.invite_stage}" on row ${index + 2}.`);
        }

        return {
          name: fullName,
          email: item.email || '',
          phone: item.phone || '',
          bio: item.bio || '',
          focus_areas: item.focus_areas || '',
          expertise: item.expertise || '',
          status: normalizedStatus,
          invite_stage: normalizedInviteStage,
        };
      })
      .filter((guide) => guide.name && guide.email);

    if (importedGuides.length === 0) {
      throw new Error('No valid Guide Partners were found. Each row needs a name and email.');
    }

    return importedGuides;
  };

  const resetImportState = useCallback(() => {
    setImportFileName('');
    setParsedImportGuides([]);
    setImportParseError('');
  }, []);

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(searchQuery && { search: searchQuery }),
      });
      const response = await adminFetch(`/admin/guest-guides?${params}`);
      if (response.ok) {
        const data = await response.json();
        setGuides(prioritizeGuides(data.guides));
        setStatusCounts(data.status_counts);
      }
    } catch (error) {
      console.error('Error fetching guides:', error);
      setGuides([]);
      setStatusCounts({});
    } finally {
      setLoading(false);
    }
  }, [adminFetch, prioritizeGuides, searchQuery, sortBy, sortOrder, statusFilter]);

  const fetchDocuments = useCallback(async () => {
    if (authLoading || !hasAdminToken) return;
    try {
      const response = await adminFetch('/admin/documents?category=guest_guide');
      if (response.ok) {
        const data = await response.json();
        const docs = (data.documents || []).map((doc) => ({
          id: doc.document_id,
          name: doc.name,
          description: doc.description,
          url: doc.content,
        }));
        setAvailableDocuments(docs);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  }, [adminFetch, authLoading, hasAdminToken]);

  const fetchSequences = useCallback(async () => {
    if (authLoading || !hasAdminToken) return;
    setLoadingSequences(true);
    try {
      const response = await adminFetch('/admin/guest-guides/email-sequences');
      if (!response.ok) {
        throw new Error('Failed to load Guide Partner email sequences');
      }

      const data = await response.json();
      const sequenceList = data.sequences || [];
      setSequences(sequenceList);
      setCampaignForm((prev) => {
        const selectedSequence =
          sequenceList.find((sequence) => sequence.sequence_id === prev.sequence_id) ||
          sequenceList[0];
        const selectedStep =
          selectedSequence?.steps?.find((step) => step.step_key === prev.step_key) ||
          selectedSequence?.steps?.[0];

        return {
          ...prev,
          sequence_id: selectedSequence?.sequence_id || '',
          step_key: selectedStep?.step_key || '',
        };
      });
      return sequenceList;
    } catch (error) {
      console.error('Failed to load Guide Partner email sequences:', error);
      setSequences([]);
      return [];
    } finally {
      setLoadingSequences(false);
    }
  }, [adminFetch, authLoading, hasAdminToken]);

  useEffect(() => {
    fetchGuides();
    fetchDocuments();
  }, [fetchDocuments, fetchGuides]);

  useEffect(() => {
    fetchSequences();
  }, [fetchSequences]);

  useEffect(() => {
    const visibleGuideIds = new Set(guides.map((guide) => guide.guide_id));
    setSelectedGuideIds((prev) => prev.filter((guideId) => visibleGuideIds.has(guideId)));
  }, [guides]);

  const selectedSequence =
    sequences.find((sequence) => sequence.sequence_id === campaignForm.sequence_id) || sequences[0] || null;
  const invitedReactivationSequence = sequences.find(
    (sequence) => sequence.system_key === 'guest_guides_invited_reactivation'
  ) || null;
  const selectedSequenceSteps = selectedSequence?.steps || [];
  const selectedStep =
    selectedSequenceSteps.find((step) => step.step_key === campaignForm.step_key) || selectedSequenceSteps[0] || null;
  const selectedStepOrder = selectedStep?.order || 0;
  const remainingSequenceSteps = selectedSequenceSteps.filter((step) => (step.order || 0) > selectedStepOrder);

  const activeCampaignGuideIds = useMemo(() => new Set(
    (activeCampaigns || [])
      .filter((campaign) => ['scheduled', 'draft_ready', 'running', 'paused'].includes((campaign?.status || '').toLowerCase()))
      .flatMap((campaign) => campaign?.recipient_ids || [])
  ), [activeCampaigns]);
  const deliveryIssuesByEmail = useMemo(() => {
    const map = new Map();
    (deliveryIssues || []).forEach((issue) => {
      const email = String(issue?.email || '').trim().toLowerCase();
      const latestStatus = String(issue?.latest_status || '').trim().toLowerCase();
      if (!email || !['suppressed', 'bounced'].includes(latestStatus) || map.has(email)) return;
      map.set(email, {
        email,
        latest_status: latestStatus,
        latest_issue_at: issue?.latest_issue_at || null,
        last_subject: issue?.last_subject || '',
        last_event_source: issue?.last_event_source || '',
      });
    });
    return map;
  }, [deliveryIssues]);
  const suppressedGuideDeliveryById = useMemo(() => {
    const map = new Map();
    guides.forEach((guide) => {
      const guideId = String(guide?.guide_id || '').trim();
      const email = String(guide?.email || '').trim().toLowerCase();
      if (!guideId || !email || map.has(guideId)) return;
      const issue = deliveryIssuesByEmail.get(email);
      if (!issue) return;
      map.set(guideId, {
        status: issue.latest_status,
        failure_label: issue.latest_status === 'suppressed' ? 'Suppressed in Resend' : 'Bounced in Resend',
        error: issue.latest_status === 'suppressed'
          ? 'Resend is suppressing this address until it is cleaned up.'
          : 'This address has bounced and should be corrected before trusting queued delivery.',
        latest_issue_at: issue.latest_issue_at,
      });
    });
    return map;
  }, [deliveryIssuesByEmail, guides]);

  const staleInvitedGuides = useMemo(() => {
    const cutoffMs = Date.now() - (STALE_INVITED_DAYS * 24 * 60 * 60 * 1000);
    return guides.filter((guide) => {
      if ((guide.invite_stage || '').toLowerCase() !== 'invited') return false;
      if (activeCampaignGuideIds.has(guide.guide_id)) return false;
      const email = String(guide.email || '').trim().toLowerCase();
      if (!email) return false;
      if (deliveryIssuesByEmail.has(email)) return false;
      const lastOutreach = guide.last_outreach_sent_at ? new Date(guide.last_outreach_sent_at) : null;
      if (!lastOutreach || Number.isNaN(lastOutreach.getTime())) return false;
      return lastOutreach.getTime() <= cutoffMs;
    });
  }, [activeCampaignGuideIds, deliveryIssuesByEmail, guides]);

  const guideAttention = useMemo(
    () =>
      deriveGuidePartnersState(
        guideSummary || {
          guides,
          activeCampaigns,
        }
      ),
    [activeCampaigns, guideSummary, guides],
  );

  const guideNextMoves = useMemo(() => {
    const items = [];
    const seenGuideIds = new Set();

    const pushGuide = (guide, config) => {
      if (!guide?.guide_id || seenGuideIds.has(guide.guide_id)) return;
      seenGuideIds.add(guide.guide_id);
      items.push({
        guide,
        ...config,
      });
    };

    guideAttention.stageLists.readyToSchedule.forEach((guide) => {
      const completedOn = formatGuideQueueDate(guide?.onboarding_completed_at);
      pushGuide(guide, {
        label: 'Schedule now',
        stageLabel: 'Ready to Schedule',
        toneClasses: 'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-300',
        detail: completedOn
          ? `Completed onboarding on ${completedOn}. Reach out personally and lock in a date.`
          : 'Completed onboarding and is waiting for a personal scheduling note.',
        primaryAction: 'schedule',
      });
    });

    guideAttention.stageLists.onboardingSentOverdue.forEach((guide) => {
      const sentOn = formatGuideQueueDate(guide?.onboarding_sent_at);
      const reminderOn = formatGuideQueueDate(guide?.onboarding_reminder_sent_at);
      pushGuide(guide, {
        label: 'Nudge onboarding',
        stageLabel: 'Onboarding Sent',
        toneClasses: 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300',
        detail: reminderOn
          ? `Reminder already went out on ${reminderOn}. Check in personally so this does not stall.`
          : sentOn
            ? `Onboarding went out on ${sentOn} and still needs a personal follow-up.`
            : 'Onboarding is overdue and needs a personal follow-up.',
        primaryAction: guide?.email ? 'email' : 'edit',
      });
    });

    guideAttention.stageLists.invitedSorted.forEach((guide) => {
      const touchOn = formatGuideQueueDate(guide?.last_outreach_sent_at);
      pushGuide(guide, {
        label: 'Keep warm',
        stageLabel: 'Invited',
        toneClasses: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-300',
        detail: touchOn
          ? `Last invite touch went out on ${touchOn}. Send the next personal note if the shared sequence is not the right fit.`
          : 'Invite is out, but this person is not moving in a queued sequence right now.',
        primaryAction: guide?.email ? 'email' : 'edit',
      });
    });

    guideAttention.stageLists.safeToInviteNow.forEach((guide) => {
      pushGuide(guide, {
        label: 'Invite now',
        stageLabel: 'Safe to Invite',
        toneClasses: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200',
        detail: 'Safe to invite without colliding with an active sequence. Use this when you want a more personal first touch.',
        primaryAction: guide?.email ? 'email' : 'edit',
      });
    });

    return items.slice(0, GUIDE_NEXT_MOVE_LIMIT);
  }, [guideAttention]);

  const readyToScheduleGuides = useMemo(
    () => guideAttention.stageLists?.readyToSchedule || [],
    [guideAttention],
  );
  const visibleReadyToScheduleGuides = useMemo(
    () => (showAllReadyToSchedule ? readyToScheduleGuides : readyToScheduleGuides.slice(0, SCHEDULING_CONTEXT_DEFAULT_LIMIT)),
    [readyToScheduleGuides, showAllReadyToSchedule],
  );
  const hasMoreReadyToSchedule = readyToScheduleGuides.length > SCHEDULING_CONTEXT_DEFAULT_LIMIT;

  const guideSections = useMemo(() => {
    const grouped = {
      ready_to_schedule: [],
      onboarding_sent: [],
      prospects_not_invited: [],
      invited: [],
      backburner: [],
      scheduled: [],
      declined: [],
      delivery_issues: [],
    };

    guides.forEach((guide) => {
      const inviteStage = normalizeGuideInviteStage(guide);
      const status = normalizeGuideStatus(guide);
      const guideId = String(guide?.guide_id || '').trim();
      const hasDeliveryIssue = guideId && suppressedGuideDeliveryById.has(guideId);

      if (hasDeliveryIssue) {
        grouped.delivery_issues.push(guide);
        return;
      }

      if (
        inviteStage === 'onboarding_completed'
        && status !== 'scheduled'
        && status !== 'completed'
        && inviteStage !== 'scheduled'
        && inviteStage !== 'completed'
      ) {
        grouped.ready_to_schedule.push(guide);
        return;
      }

      if (inviteStage === 'onboarding_sent') {
        grouped.onboarding_sent.push(guide);
        return;
      }

      if (inviteStage === 'not_invited') {
        grouped.prospects_not_invited.push(guide);
        return;
      }

      if (inviteStage === 'invited' || inviteStage === 'interested') {
        grouped.invited.push(guide);
        return;
      }

      if (inviteStage === 'backburner') {
        grouped.backburner.push(guide);
        return;
      }

      if (
        status === 'scheduled'
        || inviteStage === 'scheduled'
        || status === 'active'
        || status === 'completed'
        || inviteStage === 'completed'
      ) {
        grouped.scheduled.push(guide);
        return;
      }

      if (inviteStage === 'declined') {
        grouped.declined.push(guide);
      }
    });

    const definitions = [
      { key: 'ready_to_schedule', title: 'Ready to schedule' },
      { key: 'onboarding_sent', title: 'Onboarding sent' },
      { key: 'prospects_not_invited', title: 'Prospects / Not invited' },
      { key: 'invited', title: 'Invited' },
      { key: 'backburner', title: 'Backburner' },
      { key: 'scheduled', title: 'Scheduled' },
      { key: 'declined', title: 'Declined' },
      { key: 'delivery_issues', title: 'Delivery issues' },
    ];

    return definitions
      .map((definition) => ({
        ...definition,
        guides: grouped[definition.key] || [],
      }))
      .filter((section) => section.guides.length > 0);
  }, [guides, suppressedGuideDeliveryById]);

  const toggleGuideSection = useCallback((sectionKey) => {
    setExpandedGuideSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  const toggleReadyToScheduleExpansion = useCallback(() => {
    setShowAllReadyToSchedule((prev) => !prev);
  }, []);

  const refreshGuideViews = useCallback(async () => {
    await fetchGuides();
    if (typeof refreshGuideSummary === 'function') {
      await refreshGuideSummary();
    }
  }, [fetchGuides, refreshGuideSummary]);

  useEffect(() => {
    if (!activeCampaignGuideIds.size) return;
    setSelectedGuideIds((prev) => prev.filter((guideId) => !activeCampaignGuideIds.has(guideId)));
  }, [activeCampaignGuideIds]);

  useEffect(() => {
    if (!selectedSequence) return;

    const hasStep = selectedSequence.steps?.some((step) => step.step_key === campaignForm.step_key);
    if (!hasStep) {
      setCampaignForm((prev) => ({
        ...prev,
        sequence_id: selectedSequence.sequence_id,
        step_key: selectedSequence.steps?.[0]?.step_key || '',
      }));
      return;
    }

    if (campaignForm.sequence_id !== selectedSequence.sequence_id) {
      setCampaignForm((prev) => ({
        ...prev,
        sequence_id: selectedSequence.sequence_id,
      }));
    }
  }, [selectedSequence, campaignForm.sequence_id, campaignForm.step_key]);

  useEffect(() => {
    if (workflowKey !== 'guide-partner-outreach' || !workflowRequestKey || !outreachSectionRef.current) {
      return;
    }

    outreachSectionRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [workflowKey, workflowRequestKey]);

  useEffect(() => {
    if (!showSequenceModal || selectedGuideIds.length === 0 || !campaignForm.sequence_id || !campaignForm.step_key) {
      return;
    }

    const previewCampaign = async () => {
      setPreviewingCampaign(true);
      try {
        const response = await adminFetch('/admin/guest-guides/campaigns/preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            guide_ids: selectedGuideIds,
            sequence_id: campaignForm.sequence_id,
            step_key: campaignForm.step_key,
          }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || 'Failed to preview Guide Partner campaign');
        }

        const data = await response.json();
        setCampaignPreview(data);
      } catch (error) {
        setCampaignPreview(null);
        toast.error(error?.message || 'Failed to preview Guide Partner campaign');
      } finally {
        setPreviewingCampaign(false);
      }
    };

    previewCampaign();
  }, [adminFetch, showSequenceModal, selectedGuideIds, campaignForm.sequence_id, campaignForm.step_key]);

  const handleAddGuide = async () => {
    guestGuideAbortRef.current?.abort();
    const controller = new AbortController();
    guestGuideAbortRef.current = controller;
    setSaving(true);
    try {
      const response = await adminFetch('/admin/guest-guides', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': roleSaveIdempotencyRef.current || (roleSaveIdempotencyRef.current = crypto.randomUUID()),
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          expertise: formData.expertise_text.split(',').map((entry) => entry.trim()).filter((entry) => entry),
          birth_year: formData.birth_year ? parseInt(formData.birth_year, 10) : null,
        }),
        signal: controller.signal,
        timeoutMs: GUEST_GUIDES_TIMEOUT_MS,
      });
      if (response.ok) {
        roleSaveIdempotencyRef.current = null;
        toast.success('Guide Partner added successfully');
        setShowAddModal(false);
        resetForm();
        await refreshGuideViews();
      } else {
        const error = await response.json().catch(() => ({}));
        if (response.status === 409) roleSaveIdempotencyRef.current = null;
        toast.error(error.detail || 'Failed to add Guide Partner');
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      toast.error(error?.message === 'request timeout' ? 'Request timed out. Try again.' : 'Error adding Guide Partner');
    } finally {
      guestGuideAbortRef.current = null;
      setSaving(false);
    }
  };

  const handleUpdateGuide = async () => {
    if (!selectedGuide) return;
    guestGuideAbortRef.current?.abort();
    const controller = new AbortController();
    guestGuideAbortRef.current = controller;
    setSaving(true);
    try {
      const response = await adminFetch(`/admin/guest-guides/${selectedGuide.guide_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': roleSaveIdempotencyRef.current || (roleSaveIdempotencyRef.current = crypto.randomUUID()),
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          expertise: formData.expertise_text.split(',').map((entry) => entry.trim()).filter((entry) => entry),
          birth_year: formData.birth_year ? parseInt(formData.birth_year, 10) : null,
        }),
        signal: controller.signal,
        timeoutMs: GUEST_GUIDES_TIMEOUT_MS,
      });
      if (response.ok) {
        roleSaveIdempotencyRef.current = null;
        toast.success('Guide Partner updated successfully');
        setShowEditModal(false);
        setSelectedGuide(null);
        resetForm();
        await refreshGuideViews();
      } else {
        const error = await response.json().catch(() => ({}));
        if (response.status === 409) roleSaveIdempotencyRef.current = null;
        toast.error(error.detail || 'Failed to update Guide Partner');
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      toast.error(error?.message === 'request timeout' ? 'Request timed out. Try again.' : 'Error updating Guide Partner');
    } finally {
      guestGuideAbortRef.current = null;
      setSaving(false);
    }
  };

  const handleDeleteGuide = async (guideId) => {
    if (!confirm('Are you sure you want to delete this Guide Partner?')) return;
    try {
      const response = await adminFetch(`/admin/guest-guides/${guideId}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Guide Partner deleted');
        await refreshGuideViews();
      } else {
        toast.error('Failed to delete Guide Partner');
      }
    } catch (error) {
      toast.error('Error deleting Guide Partner');
    }
  };

  const handleSendEmail = async () => {
    if (!selectedGuide) return;
    setSaving(true);
    try {
      const response = await adminFetch(`/admin/guest-guides/${selectedGuide.guide_id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(emailData),
      });
      if (response.ok) {
        toast.success(`Email sent to ${selectedGuide.email}`);
        setShowEmailModal(false);
        setEmailData(createEmailData());
        await refreshGuideViews();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Error sending email');
    }
    setSaving(false);
  };

  const handleSchedule = async () => {
    if (!selectedGuide) return;
    setSaving(true);
    try {
      const response = await adminFetch(`/admin/guest-guides/${selectedGuide.guide_id}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          scheduled_date: new Date(scheduleData.scheduled_date).toISOString(),
          scheduled_topic: scheduleData.scheduled_topic,
        }),
      });
      if (response.ok) {
        toast.success('Presentation scheduled');
        setShowScheduleModal(false);
        setScheduleData(createScheduleData());
        await refreshGuideViews();
      } else {
        toast.error('Failed to schedule presentation');
      }
    } catch (error) {
      toast.error('Error scheduling presentation');
    }
    setSaving(false);
  };

  const handleImport = async () => {
    setSaving(true);
    try {
      if (parsedImportGuides.length === 0) {
        toast.error('Upload a CSV file and review the preview before importing.');
        return;
      }

      const response = await adminFetch('/admin/guest-guides/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ guides: parsedImportGuides }),
      });
      if (response.ok) {
        const result = await response.json();
        const importedCount = Number(result.imported_count || 0);
        const updatedCount = Number(result.updated_count || 0);
        const errorCount = Number(result.error_count || 0);

        let message = `Imported ${importedCount} new Guide Partners`;
        if (updatedCount > 0) {
          message += ` and updated ${updatedCount} existing`;
        }
        if (errorCount > 0) {
          message += `. ${errorCount} row${errorCount === 1 ? '' : 's'} had errors`;
        }

        toast.success(message);
        setShowImportModal(false);
        resetImportState();
        await refreshGuideViews();
      } else {
        toast.error('Failed to import Guide Partners');
      }
    } catch (error) {
      toast.error('Error importing Guide Partners');
    }
    setSaving(false);
  };

  const handleRunAnalysis = async () => {
    setAnalyzing(true);
    try {
      toast.info('AI analysis for Guide Partners is coming soon.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleMarkComplete = async (guide) => {
    try {
      const response = await adminFetch(`/admin/guest-guides/${guide.guide_id}/complete`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Marked as completed');
        await refreshGuideViews();
      }
    } catch (error) {
      toast.error('Error marking complete');
    }
  };

  const handleReactivate = async (guide) => {
    try {
      const response = await adminFetch(`/admin/guest-guides/${guide.guide_id}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Guide reactivated');
        await refreshGuideViews();
      }
    } catch (error) {
      toast.error('Error reactivating guide');
    }
  };

  const clearSelection = () => {
    setSelectedGuideIds([]);
  };

  const toggleGroupSelectionByInviteStage = (inviteStage) => {
    const groupGuideIds = guides
      .filter((guide) => (guide.invite_stage || 'not_invited') === inviteStage)
      .filter((guide) => !activeCampaignGuideIds.has(guide.guide_id))
      .map((guide) => guide.guide_id);

    if (groupGuideIds.length === 0) {
      toast.error('Everyone in that group is already in a queued email sequence');
      return;
    }

    setSelectedGuideIds((prev) => {
      const everyGroupGuideSelected = groupGuideIds.every((guideId) => prev.includes(guideId));
      if (everyGroupGuideSelected) {
        return prev.filter((guideId) => !groupGuideIds.includes(guideId));
      }

      const next = new Set(prev);
      groupGuideIds.forEach((guideId) => next.add(guideId));
      return Array.from(next);
    });
  };

  const openSequenceModal = async () => {
    if (selectedGuideIds.length === 0) {
      toast.error('Select at least one Guide Partner first');
      return;
    }

    if (sequences.length === 0) {
      await fetchSequences();
    }

    setCampaignPreview(null);
    setShowSequenceModal(true);
  };

  const openReactivationModal = async () => {
    let availableSequence = invitedReactivationSequence;
    if (!availableSequence) {
      const sequenceList = await fetchSequences();
      availableSequence = sequenceList.find((sequence) => sequence.system_key === 'guest_guides_invited_reactivation') || null;
    }

    if (!availableSequence) {
      toast.error('The invited reactivation email is not available yet');
      return;
    }

    const defaultGuideIds = staleInvitedGuides.map((guide) => guide.guide_id);
    if (defaultGuideIds.length === 0) {
      toast.error('No invited Guide Partners are currently stale enough for reactivation');
      return;
    }

    setReactivationForm({
      guide_ids: defaultGuideIds,
      subject: availableSequence.steps?.[0]?.subject || REACTIVATION_DEFAULT_SUBJECT,
      body: availableSequence.steps?.[0]?.body || REACTIVATION_DEFAULT_BODY,
    });
    setShowReactivationModal(true);
  };

  const closeSequenceModal = () => {
    setShowSequenceModal(false);
    setCampaignPreview(null);
  };

  const closeReactivationModal = () => {
    setShowReactivationModal(false);
    setReactivationForm(createReactivationForm());
  };

  const handleCampaignFormChange = (field, value) => {
    setCampaignForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleReactivationFormChange = (field, value) => {
    setReactivationForm((prev) => ({ ...prev, [field]: value }));
  };

  const openManualContactModal = () => {
    if (selectedGuideIds.length === 0) {
      toast.error('Select at least one Guide Partner first');
      return;
    }
    setManualContactForm(createManualContactForm());
    setShowManualContactModal(true);
  };

  const closeManualContactModal = () => {
    setShowManualContactModal(false);
    setManualContactForm(createManualContactForm());
  };

  const handleManualContactFormChange = (field, value) => {
    setManualContactForm((prev) => ({ ...prev, [field]: value }));
  };

  // Logs a contact Kevin already made himself. This sends nothing.
  const handleLogManualContact = async () => {
    if (selectedGuideIds.length === 0) {
      toast.error('Select at least one Guide Partner first');
      return;
    }

    const contactedOn = String(manualContactForm.contacted_on || '').trim();
    if (!contactedOn) {
      toast.error('Choose the date you contacted them');
      return;
    }
    if (contactedOn > todayDateInputValue()) {
      toast.error('Contact date cannot be in the future');
      return;
    }

    setManualContactSubmitting(true);
    try {
      const response = await adminFetch('/admin/guest-guides/manual-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          guide_ids: selectedGuideIds,
          contacted_on: contactedOn,
          note: String(manualContactForm.note || '').trim() || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to log the contact');
      }

      const data = await response.json();
      const markedCount = data.marked_count || 0;
      const missingCount = (data.missing_guide_ids || []).length + (data.failed_guide_ids || []).length;
      closeManualContactModal();
      clearSelection();
      await refreshGuideViews();
      toast.success(
        `Marked ${markedCount} Guide Partner${markedCount === 1 ? '' : 's'} as contacted${missingCount > 0 ? `, ${missingCount} could not be found` : ''}`
      );
    } catch (error) {
      toast.error(error?.message || 'Failed to log the contact');
    } finally {
      setManualContactSubmitting(false);
    }
  };

  const toggleReactivationGuide = (guideId) => {
    setReactivationForm((prev) => ({
      ...prev,
      guide_ids: prev.guide_ids.includes(guideId)
        ? prev.guide_ids.filter((id) => id !== guideId)
        : [...prev.guide_ids, guideId],
    }));
  };

  const handleChainDelayChange = (stepKey, value) => {
    setCampaignForm((prev) => ({
      ...prev,
      chain_delays: {
        ...prev.chain_delays,
        [stepKey]: value,
      },
    }));
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.sequence_id || !campaignForm.step_key) {
      toast.error('Choose a sequence and email step before sending');
      return;
    }

    const payload = {
      guide_ids: selectedGuideIds,
      sequence_id: campaignForm.sequence_id,
      step_key: campaignForm.step_key,
      delivery_mode: campaignForm.delivery_mode,
    };

    if (campaignForm.auto_chain_sequence && remainingSequenceSteps.length > 0) {
      payload.auto_chain_sequence = true;
      payload.chain_delays = remainingSequenceSteps.reduce((acc, step) => {
        acc[step.step_key] = Number(campaignForm.chain_delays?.[step.step_key] || 0);
        return acc;
      }, {});
    }

    if (campaignForm.delivery_mode === 'staggered') {
      const batchSize = Number(campaignForm.batch_size);
      const cadenceInterval = Number(campaignForm.cadence_interval);

      if (!campaignForm.start_date || !campaignForm.start_time) {
        toast.error('Choose a start date and time for staggered delivery');
        return;
      }

      const startAt = new Date(`${campaignForm.start_date}T${campaignForm.start_time}`);
      if (Number.isNaN(startAt.getTime())) {
        toast.error('Enter a valid staggered delivery start time');
        return;
      }

      if (!Number.isFinite(batchSize) || batchSize < 1) {
        toast.error('Batch size must be at least 1');
        return;
      }

      if (!Number.isFinite(cadenceInterval) || cadenceInterval < 1) {
        toast.error('Cadence interval must be at least 1');
        return;
      }

      payload.batch_size = batchSize;
      payload.cadence_type = campaignForm.cadence_type;
      payload.cadence_interval = cadenceInterval;
      payload.start_at = startAt.toISOString();
    }

    setCampaignSubmitting(true);
    try {
      const response = await adminFetch('/admin/guest-guides/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to create Guide Partner campaign');
      }

      const data = await response.json();
      setCampaignPreview(data.preview || null);
      clearSelection();
      setShowSequenceModal(false);
      await refreshGuideViews();
      toast.success(
        payload.delivery_mode === 'staggered'
          ? `Scheduled ${data.preview?.recipient_count || 0} Guide Partner emails${data.chained_campaigns?.length ? ` and ${data.chained_campaigns.length} follow-up step${data.chained_campaigns.length === 1 ? '' : 's'}` : ''}`
          : `Sent ${data.preview?.recipient_count || 0} Guide Partner emails${data.chained_campaigns?.length ? ` and scheduled ${data.chained_campaigns.length} follow-up step${data.chained_campaigns.length === 1 ? '' : 's'}` : ''}`
      );
    } catch (error) {
      toast.error(error?.message || 'Failed to create Guide Partner campaign');
    } finally {
      setCampaignSubmitting(false);
    }
  };

  const handleCreateReactivationCampaign = async () => {
    if (!invitedReactivationSequence) {
      toast.error('The invited reactivation email is not available yet');
      return;
    }

    const guideIds = reactivationForm.guide_ids || [];
    const subject = String(reactivationForm.subject || '').trim();
    const body = String(reactivationForm.body || '').trim();

    if (guideIds.length === 0) {
      toast.error('Select at least one Guide Partner for reactivation');
      return;
    }

    if (!subject || !body) {
      toast.error('Subject and body are both required');
      return;
    }

    setCampaignSubmitting(true);
    try {
      const response = await adminFetch('/admin/guest-guides/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          guide_ids: guideIds,
          sequence_id: invitedReactivationSequence.sequence_id,
          step_key: invitedReactivationSequence.steps?.[0]?.step_key || 'reactivation_1',
          subject_override: subject,
          body_override: body,
          delivery_mode: 'staggered',
          batch_size: 10,
          cadence_type: 'hourly_interval',
          cadence_interval: 1,
          start_at: new Date().toISOString(),
          auto_chain_sequence: false,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to create invited reactivation campaign');
      }

      const data = await response.json();
      closeReactivationModal();
      await refreshGuideViews();
      toast.success(`Started invited reactivation for ${data.preview?.recipient_count || 0} Guide Partner${(data.preview?.recipient_count || 0) === 1 ? '' : 's'}`);
    } catch (error) {
      toast.error(error?.message || 'Failed to create invited reactivation campaign');
    } finally {
      setCampaignSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(createGuideFormData());
  };

  const openEditModal = (guide) => {
    setSelectedGuide(guide);
    setFormData({
      name: guide.name || '',
      email: guide.email || '',
      phone: guide.phone || '',
      photo_url: guide.photo_url || '',
      bio: guide.bio || '',
      role: guide.role || 'Guide Partner',
      account_roles: guide.account_role_link?.guide_managed_roles || [],
      focus_areas: guide.focus_areas || [],
      expertise_text: (guide.expertise || []).join(', '),
      birth_year: guide.birth_year || '',
      location: guide.location || '',
      linkedin: guide.linkedin || '',
      facebook: guide.facebook || '',
      twitter: guide.twitter || '',
      youtube: guide.youtube || '',
      bluesky: guide.bluesky || '',
      website: guide.website || '',
      status: guide.status || 'prospect',
      invite_stage: guide.invite_stage || 'not_invited',
      notes: guide.notes || '',
    });
    setShowEditModal(true);
  };

  const handleGuidePhotoUpload = async (guideId, file) => {
    if (!guideId || !file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > GUIDE_PHOTO_MAX_BYTES) {
      toast.error('Image must be less than 3MB');
      return;
    }

    setUploadingGuidePhotoId(guideId);
    try {
      const form = new FormData();
      form.append('file', file);

      const response = await adminFetch(`/admin/guest-guides/${guideId}/photo`, {
        method: 'POST',
        body: form,
        timeoutMs: GUEST_GUIDES_TIMEOUT_MS,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to upload headshot');
        return;
      }

      const result = await response.json();
      const updatedPhotoUrl = result?.guide?.photo_url || '';
      if (selectedGuide?.guide_id === guideId) {
        setFormData((prev) => ({ ...prev, photo_url: updatedPhotoUrl }));
      }

      toast.success('Headshot uploaded');
      await refreshGuideViews();
    } catch (error) {
      toast.error('Failed to upload headshot');
    } finally {
      setUploadingGuidePhotoId(null);
    }
  };

  const openEmailModal = (guide) => {
    setSelectedGuide(guide);
    setShowEmailModal(true);
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    setImportParseError('');

    try {
      const text = await file.text();
      const importedGuides = parseImportCsv(text);
      setParsedImportGuides(importedGuides);
    } catch (error) {
      setParsedImportGuides([]);
      setImportParseError(error?.message || 'Failed to parse CSV file.');
    } finally {
      event.target.value = '';
    }
  };

  const openScheduleModal = (guide) => {
    setSelectedGuide(guide);
    setScheduleData({
      scheduled_date: guide.scheduled_date ? guide.scheduled_date.split('T')[0] : '',
      scheduled_topic: guide.scheduled_topic || '',
    });
    setShowScheduleModal(true);
  };

  const toggleFocusArea = (area) => {
    setFormData((prev) => ({
      ...prev,
      focus_areas: prev.focus_areas.includes(area)
        ? prev.focus_areas.filter((entry) => entry !== area)
        : [...prev.focus_areas, area],
    }));
  };

  const handleExpertiseChange = (value) => {
    const expertiseList = value.split(',').map((entry) => entry.trim()).filter((entry) => entry);
    setFormData((prev) => ({ ...prev, expertise: expertiseList }));
  };

  const toggleDocument = (docId) => {
    setEmailData((prev) => ({
      ...prev,
      documents: prev.documents.includes(docId)
        ? prev.documents.filter((entry) => entry !== docId)
        : [...prev.documents, docId],
    }));
  };

  const handleOpenAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const toggleGuideSelection = (guideId) => {
    if (activeCampaignGuideIds.has(guideId)) {
      toast.error('This Guide Partner is already in a queued email sequence');
      return;
    }

    setSelectedGuideIds((prev) => (
      prev.includes(guideId)
        ? prev.filter((id) => id !== guideId)
        : [...prev, guideId]
    ));
  };

  // One row per selected id, always. A guide can drop out of `guides` when the
  // filter or search changes, and the confirmation list must never quietly show
  // fewer people than it is about to mark.
  const manualContactSelectedGuides = selectedGuideIds.map((guideId) => (
    guides.find((guide) => guide.guide_id === guideId) || { guide_id: guideId, name: null }
  ));

  const selectedCount = selectedGuideIds.length;
  const sequenceRecipientCount = campaignPreview?.recipient_count || 0;
  const sequenceSkippedCount = campaignPreview?.skipped_count || 0;

  return {
    guides,
    statusCounts,
    loading,
    viewMode,
    setViewMode,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    searchQuery,
    setSearchQuery,
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    showEmailModal,
    setShowEmailModal,
    showScheduleModal,
    setShowScheduleModal,
    showImportModal,
    setShowImportModal,
    showSequenceModal,
    setShowSequenceModal,
    showReactivationModal,
    setShowReactivationModal,
    showManualContactModal,
    selectedGuide,
    setSelectedGuide,
    selectedGuideIds,
    availableDocuments,
    sequences,
    campaignPreview,
    formData,
    setFormData,
    emailData,
    setEmailData,
    scheduleData,
    setScheduleData,
    importFileName,
    parsedImportGuides,
    importParseError,
    saving,
    uploadingGuidePhotoId,
    analyzing,
    loadingSequences,
    previewingCampaign,
    campaignSubmitting,
    campaignForm,
    reactivationForm,
    manualContactForm,
    manualContactSubmitting,
    manualContactSelectedGuides,
    outreachSectionRef,
    selectedSequence,
    invitedReactivationSequence,
    selectedSequenceSteps,
    selectedStep,
    remainingSequenceSteps,
    activeCampaignGuideIds,
    suppressedGuideDeliveryById,
    guideAttention,
    guideNextMoves,
    readyToScheduleGuides: visibleReadyToScheduleGuides,
    allReadyToScheduleGuides: readyToScheduleGuides,
    hasMoreReadyToSchedule,
    showAllReadyToSchedule,
    toggleReadyToScheduleExpansion,
    guideSections,
    expandedGuideSections,
    toggleGuideSection,
    staleInvitedGuides,
    selectedCount,
    sequenceRecipientCount,
    sequenceSkippedCount,
    resetImportState,
    clearSelection,
    toggleGroupSelectionByInviteStage,
    openSequenceModal,
    openReactivationModal,
    closeSequenceModal,
    closeReactivationModal,
    handleCampaignFormChange,
    handleReactivationFormChange,
    openManualContactModal,
    closeManualContactModal,
    handleManualContactFormChange,
    handleLogManualContact,
    handleChainDelayChange,
    handleCreateCampaign,
    handleCreateReactivationCampaign,
    handleAddGuide,
    handleUpdateGuide,
    handleDeleteGuide,
    handleSendEmail,
    handleSchedule,
    handleImport,
    handleRunAnalysis,
    handleMarkComplete,
    handleReactivate,
    openEditModal,
    handleGuidePhotoUpload,
    openEmailModal,
    handleImportFileChange,
    openScheduleModal,
    toggleFocusArea,
    handleExpertiseChange,
    toggleDocument,
    toggleReactivationGuide,
    handleOpenAddModal,
    toggleGuideSelection,
    guestGuideAbortRef,
  };
}
